import React, { useState, useCallback } from 'react';
import { Container, Table, Button, Row, Col } from 'react-bootstrap';
import { UploadModal, CommentModal } from './components/modal/modal';
import ReportsService from '../../../../../../services/reports-service';
import { ExcelPreviewModal } from '../modal/excel-preview-modal/excel-preview-modal';
import * as XLSX from 'xlsx'; // Import XLSX for parsing Excel files

const EventBasedReportContainer = ({ items, handleCheckboxChange, handleSendReport, handleReject }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [uploadModalData, setUploadModalData] = useState({
    show: false,
    report: null,
    item: null,
  });
  const [excelPreviewData, setExcelPreviewData] = useState([]); // State for Excel preview data
  const [showExcelPreviewModal, setShowExcelPreviewModal] = useState(false); // State to control modal visibility
  const [commentModalData, setCommentModalData] = useState({ show: false, comment: '' });

  const reportService = new ReportsService(); 

  const handleUploadModalClose = useCallback(() => {
    setUploadModalData({ show: false });
  }, []);

  const getEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.toLowerCase();
    } catch (e) {
      console.log(e);
    }
    return url;
  };

  // New handlePreviewClick function for Excel files
  const handlePreviewClick = async (report) => {
    try {
      let s3Prefix = '';
      if (
        report.reportTitle.startsWith('Closure of Registry Service') ||
        report.reportTitle.startsWith('Consent Solicitation Entitlement') ||
        report.reportTitle.startsWith('Credit Letter')
      ) {
        s3Prefix = `Ad-hoc Reports/${report.isin}/${report.reportTitle}/${report.paymentDate}/`;
      } else {
        s3Prefix = `Ad-hoc Reports/${report.isin}/${report.reportTitle}/${report.bpId}/${report.holder}/${report.dateFrom + report.dateTo}/`;
      }
      const response = await reportService.retrieveEventBasedReport(s3Prefix);
      const fileURL = response.fileURL;

      // Check if the file is an Excel file
      const urlObj = new URL(fileURL);
      if (urlObj.pathname.toLowerCase().endsWith('.xlsx')) {
        const fileResponse = await fetch(fileURL);
        const blob = await fileResponse.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const spreadsheetData = jsonData.map(row => row.map(cell => ({ value: cell, readOnly: true })));
        setExcelPreviewData(spreadsheetData);
        setShowExcelPreviewModal(true);
      } else {
        const embedUrl = getEmbedUrl(fileURL);
        const newWindow = window.open(embedUrl, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      }
    } catch (error) {
      console.error('Error fetching report preview:', error.message);
    }
  };  

  // Handle the download action remains unchanged
  const handleDownloadClick = async (report) => {
    try {
      let s3Prefix = '';
      if (
        report.reportTitle.startsWith('Closure of Registry Service') ||
        report.reportTitle.startsWith('Consent Solicitation Entitlement') ||
        report.reportTitle.startsWith('Credit Letter')
      ) {
        s3Prefix = `Ad-hoc Reports/${report.isin}/${report.reportTitle}/${report.paymentDate}/`;
      } else {
        s3Prefix = `Ad-hoc Reports/${report.isin}/${report.reportTitle}/${report.bpId}/${report.holder}/${report.dateFrom + report.dateTo}/`;
      }
      const response = await reportService.retrieveEventBasedReport(s3Prefix);
      
      if (response && response.fileURL) {
        const url = response.fileURL;
        const filename = response.fileName || url.substring(url.lastIndexOf('/') + 1);
  
        const fileResponse = await fetch(url);
        if (!fileResponse.ok) throw new Error('Network response was not ok');
  
        const blob = await fileResponse.blob();
        const objectUrl = window.URL.createObjectURL(blob);
  
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        console.error('Download: No file URL returned');
      }
    } catch (error) {
      console.error('Error fetching report download:', error);
    }
  };

  const handleUploadClick = useCallback((item) => {
    setUploadModalData({ show: true, report: null, item });
  }, []);

  const handleLocalCheckboxChange = (id) => (e) => {
    if (e.target.checked) {
      setSelectedItems((prev) => [...prev, id]);
    } else {
      setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
    }
    if (handleCheckboxChange) {
      handleCheckboxChange(id);
    }
  };

  return (
    <Container
      fluid
      className="event-based-report-container"
      style={{ display: 'flex', flexDirection: 'column', height: '70vh' }}
    >
      <h5>Event-Based Container</h5>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Table bordered>
          <thead>
            <tr>
              <th></th>
              <th>Report Name</th>
              <th>Issuer</th>
              <th>ISIN</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items && items.length > 0 ? (
              items.map((item, index) => (
                <tr key={index}>
                  <td>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        style={{ marginRight: '8px' }}
                        disabled={item.status !== 'For Checking'}
                        checked={selectedItems.includes(item.id)}
                        onChange={handleLocalCheckboxChange(item.id)}
                      />
                    </label>
                  </td>
                  <td>
                    {item.reportTitle === 'BIR 2306 Request Letter' ? (
                      <>
                        {'BIR 2306 Request Letter'}<br />
                        {`BPID: ${item.bpId || ''}`}<br />
                        {`Holder Name: ${item.holder || ''}`}
                      </>
                    ) : item.reportTitle === 'Closure of Registry Service / Payment Account' ? (
                      <>
                        {'Closure of Registry Service / Payment Account'}<br />
                        {`Maturity Date: ${item.maturityDate || ''}`}
                      </>
                    ) : item.reportTitle === 'Credit Letter' ? (
                      <>
                        {'Credit Letter'}<br />
                        {`Payment Date: ${item.paymentDate || ''}`}
                      </>
                    ) : (
                      item.reportTitle
                    )}
                  </td>
                  <td>{item.securityIssuer}</td>
                  <td>{item.isin}</td>
                  <td>
                    {item.status}
                    {item.comment && (
                      <span
                        style={{ cursor: 'pointer', marginLeft: '5px' }}
                        onClick={() => setCommentModalData({ show: true, comment: item.comment })}
                      >
                        &#9865;
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {(item.status === 'Report Available' || item.status === 'For Checking') && (
                        <Button variant="primary" className="action-button" onClick={() => handleUploadClick(item)}>
                          Upload
                        </Button>
                      )}
                      {(item.status !== 'For Generation' || item.status === 'For Checking') && (
                        <>
                          <Button variant="primary" className="action-button" onClick={() => handlePreviewClick(item)}>
                            Preview
                          </Button>
                          <Button variant="primary" className="action-button" onClick={() => handleDownloadClick(item)}>
                            Download
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  No Reports
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <Row className="mt-3">
        <Col className="d-flex justify-content-end">
          <Button
            variant="danger"
            className="mr-2"
            onClick={handleReject || (() => {})}
          >
            Reject Report
          </Button>
          <Button
            variant="primary"
            onClick={handleSendReport || (() => {})}
          >
            Publish Report to Issuer
          </Button>
        </Col>
      </Row>

      <UploadModal
        report={uploadModalData.report}
        item={uploadModalData.item}
        show={uploadModalData.show}
        onClose={handleUploadModalClose}
      />

      <CommentModal
        show={commentModalData.show}
        comment={commentModalData.comment}
        onClose={() => setCommentModalData({ show: false, comment: '' })}
      />

      {/* Excel Preview Modal */}
      {showExcelPreviewModal && (
        <ExcelPreviewModal
          show={showExcelPreviewModal}
          data={excelPreviewData}
          onClose={() => setShowExcelPreviewModal(false)}
        />
      )}
    </Container>
  );
};

export default EventBasedReportContainer;
